/** @format */

import { type ReactElement, useState, useEffect, useCallback } from "react";
import {
  Cpu,
  Loader,
  Database,
  Box,
  FileText,
  Video,
  Camera,
  // config option icons
  Layers,
  ListOrdered,
  ListChecks,
  Ruler,
  Gauge,
  LayoutGrid,
  Film,
  Activity,
  Clock,
  ScanSearch,
  Type,
  ShieldCheck,
  // sop type icons
  Package,
  ClipboardCheck,
  Boxes,
  Truck,
  Wrench,
  Scissors,
} from "lucide-react";
import "./GenerateSOP.css";
import FreeTrialNotif from "./FreeTrialNotif";
import useFreeTrials from "../../hooks/useFreeTrials";
import Header from "../../components/ui/Header";
import HeroSection from "../../components/ui/HeroSection";
import "../../components/ui/Header.css";
import useSopAnalyze from "../../hooks/useSopAnalyze";
import { track } from "../../lib/posthog";
import { useToast } from "../../hooks/useToast";
import Toast from "../../components/ui/Toast";
import "../../components/ui/Toast.css";
import FlowCard from "../../components/ui/FlowCard";
import {
  FLAG_CAD_PIPELINE,
  FLAG_DRAWINGS_PIPELINE,
  FLAG_IMAGE_PIPELINE,
  FLAG_VIDEO_PIPELINE,
  useFeatureFlagEnabled,
} from "../../lib/featureFlags";
import FlowUploader from "../../components/ui/FlowUploader";
import FlowConfig, { FlowConfigOption } from "../../components/ui/FlowConfig";
import SopTypeSelector, {
  SopTypeOption,
} from "../../components/ui/SopTypeSelector";
import { useOrganizationStore } from "../../store/useOrganizationStore";

/**
 * The shape of the analysis result. Replace `object` with a more specific interface
 * once you know the exact structure returned by useSopAnalyze.
 */
export type SopAnalysisResult = object | null;

export type FlowType = "cad" | "drawings" | "video" | "image";

interface FlowDefinition {
  id: FlowType;
  title: string;
  description: string;
  icon: React.ReactNode;
  fileTypes: string[];
  acceptedMimeTypes: string[];
  maxFiles: number;
  configOptions: FlowConfigOption[];
}

const FLOWS: Record<FlowType, FlowDefinition> = {
  cad: {
    id: "cad",
    title: "CAD Model",
    description:
      "STEP/STP files for assembly procedures from 3D models",
    icon: <Box className="w-8 h-8" />,
    fileTypes: [".stp", ".step"],
    acceptedMimeTypes: ["application/octet-stream", ".stp", ".step"],
    maxFiles: 5,
    configOptions: [
      {
        id: "explosion_diagrams",
        label: "Explosion diagrams",
        description: "Exploded views for complex assemblies",
        default: true,
        icon: Layers,
      },
      {
        id: "assembly_sequence",
        label: "Assembly sequence",
        description: "Step-by-step assembly instructions",
        default: true,
        icon: ListOrdered,
      },
      {
        id: "bom_extraction",
        label: "Extract BOM",
        description: "Bill of materials from model",
        default: false,
        icon: ListChecks,
      },
    ],
  },
  drawings: {
    id: "drawings",
    title: "Assembly Drawings",
    description:
      "PDF or images of drawings and schematics",
    icon: <FileText className="w-8 h-8" />,
    fileTypes: [".pdf", ".png", ".jpeg", ".webp"],
    acceptedMimeTypes: [
      "application/pdf",
      "image/png",
      "image/jpeg",
      "image/webp",
    ],
    maxFiles: 10,
    configOptions: [
      {
        id: "dimension_extraction",
        label: "Extract dimensions",
        description: "Identify and extract dimensional information",
        default: true,
        icon: Ruler,
      },
      {
        id: "tolerance_analysis",
        label: "Tolerance analysis",
        description: "Tolerance specifications in drawings",
        default: false,
        icon: Gauge,
      },
      {
        id: "view_generation",
        label: "Multiple views",
        description: "Front, side, and isometric views",
        default: true,
        icon: LayoutGrid,
      },
    ],
  },
  video: {
    id: "video",
    title: "Video Workflow",
    description: "Video clips to extract steps and workflows",
    icon: <Video className="w-8 h-8" />,
    fileTypes: [".mp4", ".mov", ".avi", ".mkv"],
    acceptedMimeTypes: [
      "video/mp4",
      "video/quicktime",
      "video/avi",
      "video/mkv",
    ],
    maxFiles: 10,
    configOptions: [
      {
        id: "frame_extraction",
        label: "Key frame extraction",
        description: "Pick important frames to document each step",
        default: true,
        icon: Film,
      },
      {
        id: "motion_analysis",
        label: "Motion analysis",
        description: "Hand movements and tool usage",
        default: true,
        icon: Activity,
      },
      {
        id: "timeline_generation",
        label: "Generate timeline",
        description: "Chronological step sequence",
        default: false,
        icon: Clock,
      },
    ],
  },
  image: {
    id: "image",
    title: "Real-world Image",
    description:
      "Photos of processes, equipment, or workstations",
    icon: <Camera className="w-8 h-8" />,
    fileTypes: [".png", ".jpeg", ".webp"],
    acceptedMimeTypes: ["image/png", "image/jpeg", "image/webp"],
    maxFiles: 15,
    configOptions: [
      {
        id: "object_detection",
        label: "Object detection",
        description: "Identify tools, parts, and equipment",
        default: true,
        icon: ScanSearch,
      },
      {
        id: "text_extraction",
        label: "Text extraction",
        description: "Read labels, signs, and displays",
        default: false,
        icon: Type,
      },
      {
        id: "safety_analysis",
        label: "Safety analysis",
        description: "Identify safety equipment and potential hazards",
        default: true,
        icon: ShieldCheck,
      },
    ],
  },
};

export default function GenerateSOP(): ReactElement {
  const cadPipelineEnabled = useFeatureFlagEnabled(FLAG_CAD_PIPELINE);
  const drawingsPipelineEnabled = useFeatureFlagEnabled(FLAG_DRAWINGS_PIPELINE);
  const videoPipelineEnabled = useFeatureFlagEnabled(FLAG_VIDEO_PIPELINE);
  const imagePipelineEnabled = useFeatureFlagEnabled(FLAG_IMAGE_PIPELINE);

  const [selectedFlow, setSelectedFlow] = useState<FlowType | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  // TODO use selectedOptions to change analysis parameters
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [selectedSopType, setSelectedSopType] = useState<string | null>(
    "final-assembly-test",
  );
  const [openFreeTrialNotif, setOpenFreeTrialNotif] = useState(false);
  const [fileNotes, setFileNotes] = useState<Record<string, string>>({});

  const [assemblyFile, setAssemblyFile] = useState<File | null>(null);
  const { toast, showToast, hideToast } = useToast();
  const organizations = useOrganizationStore((s) => s.organizations);
  const activeOrgId = useOrganizationStore((s) => s.activeOrgId);
  const activeOrg = organizations.find((o) => o.orgId === activeOrgId);
  const maxUploadBytes = activeOrg?.maxUploadBytes ?? null;
  const onSubmitSuccess = useCallback(() => {
    showToast(
      "Success",
      "Your files have been submitted for processing.",
      "success",
    );
  }, [showToast]);
  const {
    analyzeFile,
    analyzing,
    progressMessage,
    progressPercentage,
    error,
    formattedElapsedTime,
  } = useSopAnalyze(
    files,
    "",
    fileNotes,
    assemblyFile,
    onSubmitSuccess,
  );
  const {
    freeTrialsRemaining,
    freeTrialLimit,
    isFreeTrial,
    getFreeTrialsUsed,
  } = useFreeTrials();

  const sopTypeOptions: SopTypeOption[] = [
    {
      id: "final-assembly-test",
      label: "Final Assembly & Test",
      description: "Assembly and test for finished products",
      icon: Package,
    },
    {
      id: "incoming-quality-control",
      label: "Incoming Quality Control",
      description: "Incoming materials and components inspection",
      icon: ClipboardCheck,
    },
    {
      id: "sub-assembly",
      label: "Sub-Assembly",
      description: "Procedures for sub-components and modules",
      disabled: true,
      icon: Boxes,
    },
    {
      id: "shipping-packaging",
      label: "Shipping & Packaging",
      description: "Packaging and shipping procedures",
      disabled: true,
      icon: Truck,
    },
    {
      id: "troubleshooting",
      label: "Troubleshooting",
      description: "Diagnostic and repair for common issues",
      disabled: true,
      icon: Wrench,
    },
    {
      id: "disassembly",
      label: "Disassembly",
      description: "Steps to remove and disassemble components",
      disabled: true,
      icon: Scissors,
    },
  ];

  // Show notification if user is on free trial
  useEffect(() => {
    if (isFreeTrial) {
      setOpenFreeTrialNotif(true);
    }
  }, [isFreeTrial]);

  const handleFlowSelect = (flowType: FlowType) => {
    const flowAccess: Record<FlowType, boolean> = {
      cad: cadPipelineEnabled,
      drawings: drawingsPipelineEnabled,
      video: videoPipelineEnabled,
      image: imagePipelineEnabled,
    };
    if (flowAccess[flowType] === false) {
      showToast(
        "Feature unavailable",
        `${FLOWS[flowType].title} pipeline is disabled for your organization.`,
        "error",
      );
      return;
    }
    setSelectedFlow(flowType);
    setFiles([]);
    setSelectedOptions([]);
    setSelectedSopType("final-assembly-test");
    setFileNotes({});
    setAssemblyFile(null);
  };

  useEffect(() => {
    if (!selectedFlow) return;
    const flowAccess: Record<FlowType, boolean> = {
      cad: cadPipelineEnabled,
      drawings: drawingsPipelineEnabled,
      video: videoPipelineEnabled,
      image: imagePipelineEnabled,
    };
    if (flowAccess[selectedFlow] === false) {
      setSelectedFlow(null);
    }
  }, [
    selectedFlow,
    cadPipelineEnabled,
    drawingsPipelineEnabled,
    videoPipelineEnabled,
    imagePipelineEnabled,
  ]);

  const handleMoveLeft = useCallback(
    (index: number) => {
      if (index > 0) {
        const newFiles = [...files];
        [newFiles[index - 1], newFiles[index]] = [
          newFiles[index],
          newFiles[index - 1],
        ];
        setFiles(newFiles);
      }
    },
    [files],
  );

  const handleMoveRight = useCallback(
    (index: number) => {
      if (index < files.length - 1) {
        const newFiles = [...files];
        [newFiles[index], newFiles[index + 1]] = [
          newFiles[index + 1],
          newFiles[index],
        ];
        setFiles(newFiles);
      }
    },
    [files],
  );

  const handleFilesChange = useCallback(
    async (newFiles: File[]) => {
      const allowedFiles = maxUploadBytes
        ? newFiles.filter((file) => file.size <= maxUploadBytes)
        : newFiles;
      if (allowedFiles.length !== newFiles.length && maxUploadBytes) {
        const maxMb = (maxUploadBytes / 1024 / 1024).toFixed(1);
        showToast(
          "Upload blocked",
          `Your demo plan allows files up to ${maxMb} MB. Contact support for a higher limit.`,
          "error",
        );
        track("demo_upload_blocked_size", { maxUploadBytes });
      }
      setFiles(allowedFiles);

      if (files.length < allowedFiles.length) {
        setTimeout(() => {
          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: "smooth",
          });
        }, 100);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [selectedFlow, files.map((f) => f.name).join(","), maxUploadBytes, showToast],
  ); // Use file names as stable dependency

  const handleFileNoteChange = useCallback((fileName: string, note: string) => {
    setFileNotes((prev) => ({
      ...prev,
      [fileName]: note,
    }));
  }, []);

  const handleAssemblyChange = useCallback((file: File | null) => {
    setAssemblyFile(file);
  }, []);

  const handleAnalyze = async () => {
    if (selectedFlow && files.length > 0 && selectedSopType) {
      track("sop_analysis_started", {
        flow_type: selectedFlow,
        sop_type: selectedSopType,
        file_count: files.length,
        source: "generate_sop",
      });

      setTimeout(() => {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        });
      }, 100);

      // Fetch latest subscription status before starting analysis
      await getFreeTrialsUsed();
      // TODO: Use selectedOptions to modify analysis parameters
      console.log("Selected options:", selectedOptions);
      console.log("File notes:", fileNotes);
      analyzeFile();
    }
  };

  const currentFlow = selectedFlow ? FLOWS[selectedFlow] : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-950 to-black text-white">
      <Header />
      <div className="min-h-screen pt-20 overflow-x-hidden">
        <main className="pt-8 w-full mx-auto px-6 lg:px-10">
          <div className="max-w-[1440px] mx-auto pb-8">
            {/* Hero Section */}
            <HeroSection
              title="Generate SOPs"
              description="AI-powered analysis and documentation for manufacturing."
            />

            <div className="bg-gradient-to-br from-gray-900/20 to-black/40 backdrop-blur-xl border border-gray-800/15 rounded-2xl pb-8 shadow-2xl shadow-black/50">
              <FreeTrialNotif
                open={openFreeTrialNotif}
                onOpen={() => { }}
                freeTrialsRemaining={freeTrialsRemaining}
                freeTrialLimit={freeTrialLimit}
              />

              {/* Flow Selection */}
              {!selectedFlow && (
                <div className="space-y-8">
                  <div className="text-center mb-8">
                    <h2 className="text-xl font-bold mb-3 text-slate-200">
                      Select content type to analyze
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                    {Object.values(FLOWS).map((flow) => {
                      const flowAccess: Record<FlowType, boolean> = {
                        cad: cadPipelineEnabled,
                        drawings: drawingsPipelineEnabled,
                        video: videoPipelineEnabled,
                        image: imagePipelineEnabled,
                      };
                      const isGated = flowAccess[flow.id] === false;
                      return (
                        <FlowCard
                          key={flow.id}
                          title={flow.title}
                          description={flow.description}
                          icon={flow.icon}
                          fileTypes={flow.fileTypes}
                          onClick={() => handleFlowSelect(flow.id)}
                          disabled={isGated}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Flow-specific Upload and Configuration */}
              {selectedFlow && currentFlow && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between mb-4">
                    <button
                      onClick={() => setSelectedFlow(null)}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 cursor-pointer bg-slate-600/10 border border-slate-600/20 text-slate-300 hover:bg-slate-600/20 hover:border-slate-600/30 hover:text-slate-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-slate-600/10"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                      <span>Back</span>
                    </button>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center">
                        {currentFlow.icon}
                      </div>
                      <h2 className="text-2xl font-semibold text-white">
                        {currentFlow.title}
                      </h2>
                    </div>
                  </div>

                  <div className="flex flex-col lg:flex-row gap-6">
                    {/* Left panel: config + type */}
                    <div className="w-full lg:w-[380px] shrink-0 space-y-5">
                      <div>
                        <div className="flex items-center gap-2 mb-2 px-1">
                          <span className="w-5 h-5 rounded-full bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-[10px] font-bold text-blue-400">1</span>
                          <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Configuration</span>
                        </div>
                        <FlowConfig
                          flowType={currentFlow.title}
                          options={currentFlow.configOptions}
                          onOptionsChange={setSelectedOptions}
                        />
                      </div>

                      <div>
                        <div className="flex items-center gap-2 mb-2 px-1">
                          <span className="w-5 h-5 rounded-full bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-[10px] font-bold text-blue-400">2</span>
                          <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Document Type</span>
                        </div>
                        <SopTypeSelector
                          options={sopTypeOptions}
                          selectedType={selectedSopType}
                          onTypeChange={setSelectedSopType}
                        />
                      </div>
                    </div>

                    {/* Right panel: file upload + actions */}
                    <div className="flex-1 min-w-0 pt-7">
                      <FlowUploader
                        flowType={currentFlow.id}
                        fileTypes={currentFlow.fileTypes}
                        acceptedMimeTypes={currentFlow.acceptedMimeTypes}
                        maxFiles={currentFlow.maxFiles}
                        onFilesChange={handleFilesChange}
                        files={files}
                        onMoveLeft={handleMoveLeft}
                        onMoveRight={handleMoveRight}
                        fileNotes={fileNotes}
                        onFileNoteChange={handleFileNoteChange}
                        assemblyFile={assemblyFile}
                        onAssemblyChange={handleAssemblyChange}
                        maxUploadBytes={maxUploadBytes}
                        actionSlot={
                          analyzing ? (
                            <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 backdrop-blur-sm px-5 py-4 space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <Loader className="animate-spin w-4 h-4 text-blue-400" />
                                  <span className="text-sm font-medium text-white">{progressMessage}</span>
                                </div>
                                <span className="text-xs text-slate-400">
                                  {Math.round(progressPercentage)}% • {formattedElapsedTime}
                                </span>
                              </div>
                              <div className="w-full h-1.5 bg-slate-700/30 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full transition-all duration-500 ease-out"
                                  style={{ width: `${progressPercentage}%` }}
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-700/30 bg-slate-900/30 px-5 py-3">
                              <div className="flex items-center gap-2 flex-wrap">
                                {files.length > 0 && (
                                  <span className="inline-flex items-center gap-1.5 text-xs bg-slate-800/60 text-slate-300 px-2.5 py-1 rounded-full border border-slate-700/30">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                                    {files.length} file{files.length !== 1 ? "s" : ""}
                                  </span>
                                )}
                                {selectedSopType && (
                                  <span className="inline-flex items-center gap-1.5 text-xs bg-slate-800/60 text-slate-300 px-2.5 py-1 rounded-full border border-slate-700/30">
                                    <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                                    {sopTypeOptions.find((o) => o.id === selectedSopType)?.label}
                                  </span>
                                )}
                                {assemblyFile && (
                                  <span className="inline-flex items-center gap-1.5 text-xs bg-slate-800/60 text-slate-300 px-2.5 py-1 rounded-full border border-slate-700/30">
                                    <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                                    Assembly
                                  </span>
                                )}
                                <button
                                  className="inline-flex items-center gap-1.5 text-xs text-slate-500 cursor-not-allowed ml-1"
                                  disabled
                                >
                                  <Database className="w-3.5 h-3.5" />
                                  PLM
                                </button>
                              </div>
                              <button
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm whitespace-nowrap transition-all duration-200 ${
                                  !files.length ||
                                  !selectedSopType ||
                                  analyzing ||
                                  (!assemblyFile && ["cad", "video"].includes(currentFlow.id))
                                    ? "opacity-50 cursor-not-allowed bg-slate-600/10 border border-slate-600/20 text-slate-400"
                                    : "cursor-pointer bg-gradient-to-r from-blue-500/20 to-blue-500/10 border border-blue-500/30 text-blue-400 hover:from-blue-500/30 hover:to-blue-500/20 hover:border-blue-500/40 hover:text-blue-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/20"
                                }`}
                                onClick={handleAnalyze}
                                disabled={
                                  !files.length ||
                                  !selectedSopType ||
                                  analyzing ||
                                  (!assemblyFile && ["cad", "video"].includes(currentFlow.id))
                                }
                              >
                                <Cpu className="w-4 h-4" />
                                <span>Analyze {currentFlow.title}</span>
                              </button>
                            </div>
                          )
                        }
                      />

                      {error && !analyzing && (
                        <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
                          <div className="flex items-center gap-2">
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            <span className="font-medium">Error</span>
                          </div>
                          <p className="mt-2 text-sm">{error}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
      <Toast
        open={toast.open}
        onOpenChange={hideToast}
        title={toast.title ?? ""}
        description={toast.description}
        type={toast.type}
        duration={toast.duration}
      />
    </div>
  );
}
